import { CooldownManager, SpellType } from '../game/CooldownManager';
import { WizardType, WIZARD_CONFIGS } from './WizardTypes';

export type CPUAction = {
  type: 'cast';
  spell: SpellType;
} | {
  type: 'idle';
};

interface PerformanceMetrics {
  playerHits: number;
  playerMisses: number;
  cpuHits: number;
  cpuMisses: number;
  playerWins: number;
  cpuWins: number;
  totalRounds: number;
}

// Wizard-specific AI personality profiles
interface AIPersonality {
  aggressiveness: number;    // 0-1, how often to attack vs defend
  shieldBias: number;        // 0-1, tendency to shield when threatened
  preferredSpell: SpellType; // Spell this personality favors
  spellBias: number;         // Weight multiplier for preferred spell
  counterAttackBias: number; // 0-1, chance to counter after blocking
  patternExploitRate: number; // 0-1, how much AI uses pattern prediction
}

const AI_PERSONALITIES: Record<WizardType, AIPersonality> = {
  pyromancer: {
    aggressiveness: 0.75,
    shieldBias: 0.15,
    preferredSpell: 'fireball',
    spellBias: 2.0,
    counterAttackBias: 0.7,
    patternExploitRate: 0.4,
  },
  frostmage: {
    aggressiveness: 0.4,
    shieldBias: 0.55,
    preferredSpell: 'iceShard',
    spellBias: 1.8,
    counterAttackBias: 0.3,
    patternExploitRate: 0.6,
  },
  battlemage: {
    aggressiveness: 0.45,
    shieldBias: 0.6,
    preferredSpell: 'shield',
    spellBias: 1.5,
    counterAttackBias: 0.5,
    patternExploitRate: 0.3,
  },
  archmage: {
    aggressiveness: 0.55,
    shieldBias: 0.35,
    preferredSpell: 'fireball',
    spellBias: 1.2,
    counterAttackBias: 0.5,
    patternExploitRate: 0.5,
  },
  necromancer: {
    aggressiveness: 0.7,
    shieldBias: 0.2,
    preferredSpell: 'fireball',
    spellBias: 1.6,
    counterAttackBias: 0.6,
    patternExploitRate: 0.45,
  },
  druid: {
    aggressiveness: 0.35,
    shieldBias: 0.5,
    preferredSpell: 'iceShard',
    spellBias: 1.6,
    counterAttackBias: 0.3,
    patternExploitRate: 0.4,
  },
  chronomancer: {
    aggressiveness: 0.65,
    shieldBias: 0.3,
    preferredSpell: 'lightningBolt',
    spellBias: 1.4,
    counterAttackBias: 0.6,
    patternExploitRate: 0.55,
  },
};

export class CPUOpponent {
  private wizardType: WizardType;
  private personality: AIPersonality;
  private cooldowns: Map<SpellType, number> = new Map();
  private cooldownManager: CooldownManager | null = null;
  private lastActionTime: number = 0;
  private minActionDelay: number = 1500;
  private maxActionDelay: number = 3000;
  private currentDelay: number = 2000;

  private reactionTime: number = 300;
  private aggressiveness: number = 0.5;
  private shieldProbability: number = 0.3;

  private incomingProjectile: boolean = false;
  private incomingProjectileTime: number = 0;

  // Pattern recognition: track player's recent spells
  private playerSpellHistory: { spell: SpellType; time: number }[] = [];
  private static readonly PATTERN_HISTORY_SIZE = 8;

  // Shield-counter tracking
  private justShielded: boolean = false;
  private shieldTime: number = 0;
  private static readonly COUNTER_WINDOW_MS = 2000;

  // Track enemy (player) shield state for tactical decisions
  private playerShieldActive: boolean = false;

  private metrics: PerformanceMetrics = {
    playerHits: 0,
    playerMisses: 0,
    cpuHits: 0,
    cpuMisses: 0,
    playerWins: 0,
    cpuWins: 0,
    totalRounds: 0
  };

  constructor(wizardType: WizardType) {
    this.wizardType = wizardType;
    this.personality = AI_PERSONALITIES[wizardType];
    this.aggressiveness = this.personality.aggressiveness;
    this.shieldProbability = this.personality.shieldBias;
    this.initializeCooldowns();
  }

  setCooldownManager(manager: CooldownManager): void {
    this.cooldownManager = manager;
  }

  private initializeCooldowns(): void {
    this.cooldowns.set('fireball', 0);
    this.cooldowns.set('iceShard', 0);
    this.cooldowns.set('shield', 0);
    this.cooldowns.set('lightningBolt', 0);
    this.cooldowns.set('healSpell', 0);
    this.cooldowns.set('meteor', 0);
    this.cooldowns.set('superShield', 0);
    this.cooldowns.set('blizzard', 0);
  }

  setWizardType(type: WizardType): void {
    this.wizardType = type;
    this.personality = AI_PERSONALITIES[type];
    this.aggressiveness = this.personality.aggressiveness;
    this.shieldProbability = this.personality.shieldBias;
  }

  notifyIncomingProjectile(): void {
    this.incomingProjectile = true;
    this.incomingProjectileTime = Date.now();
  }

  // Called when the player casts a spell so the AI can learn patterns
  recordPlayerSpell(spell: SpellType): void {
    this.playerSpellHistory.push({ spell, time: Date.now() });
    if (this.playerSpellHistory.length > CPUOpponent.PATTERN_HISTORY_SIZE) {
      this.playerSpellHistory.shift();
    }
  }

  // Notify AI of player shield state for tactical decisions
  notifyPlayerShieldState(active: boolean): void {
    this.playerShieldActive = active;
  }

  recordPlayerHit(): void {
    this.metrics.playerHits++;
    this.adjustDifficulty();
  }

  recordPlayerMiss(): void {
    this.metrics.playerMisses++;
    this.adjustDifficulty();
  }

  recordCPUHit(): void {
    this.metrics.cpuHits++;
    this.adjustDifficulty();
  }

  recordCPUMiss(): void {
    this.metrics.cpuMisses++;
    this.adjustDifficulty();
  }

  recordGameEnd(playerWon: boolean): void {
    this.metrics.totalRounds++;
    if (playerWon) {
      this.metrics.playerWins++;
    } else {
      this.metrics.cpuWins++;
    }
    this.adjustDifficulty();
  }

  private adjustDifficulty(): void {
    const playerAccuracy = this.metrics.playerHits / Math.max(1, this.metrics.playerHits + this.metrics.playerMisses);
    const playerWinRate = this.metrics.playerWins / Math.max(1, this.metrics.totalRounds);

    if (playerWinRate > 0.7 || playerAccuracy > 0.6) {
      this.reactionTime = Math.max(100, this.reactionTime - 20);
      this.aggressiveness = Math.min(0.8, this.aggressiveness + 0.05);
      this.minActionDelay = Math.max(1000, this.minActionDelay - 100);
    } else if (playerWinRate < 0.3 || playerAccuracy < 0.3) {
      this.reactionTime = Math.min(400, this.reactionTime + 30);
      this.aggressiveness = Math.max(0.3, this.aggressiveness - 0.05);
      this.minActionDelay = Math.min(2500, this.minActionDelay + 150);
    }

    this.shieldProbability = Math.max(
      this.personality.shieldBias,
      0.2 + (playerAccuracy * 0.4)
    );
  }

  // Predict what the player is most likely to cast next based on recent history
  private predictPlayerNextSpell(): SpellType | null {
    if (this.playerSpellHistory.length < 2) return null;

    // Count spell frequencies in recent history
    const counts: Record<string, number> = { fireball: 0, iceShard: 0, shield: 0, lightningBolt: 0, healSpell: 0 };
    for (const entry of this.playerSpellHistory) {
      counts[entry.spell] = (counts[entry.spell] || 0) + 1;
    }

    // Check for sequences (player repeating the same spell)
    const lastTwo = this.playerSpellHistory.slice(-2);
    if (lastTwo[0].spell === lastTwo[1].spell) {
      // Player is repeating - they'll likely do it again
      return lastTwo[0].spell;
    }

    // Check for alternating patterns (A-B-A-B)
    if (this.playerSpellHistory.length >= 4) {
      const lastFour = this.playerSpellHistory.slice(-4);
      if (lastFour[0].spell === lastFour[2].spell && lastFour[1].spell === lastFour[3].spell) {
        // Alternating pattern detected - predict next in sequence
        return lastFour[2].spell;
      }
    }

    // Return most used spell
    let maxSpell: SpellType = 'fireball';
    let maxCount = 0;
    for (const [spell, count] of Object.entries(counts)) {
      if ((count as number) > maxCount) {
        maxCount = count as number;
        maxSpell = spell as SpellType;
      }
    }
    return maxSpell;
  }

  // Choose the best counter for a predicted player spell
  private getCounterSpell(playerSpell: SpellType): SpellType {
    switch (playerSpell) {
      case 'fireball':
      case 'lightningBolt':
      case 'meteor':
      case 'blizzard':
        // Counter offensive spells with shield or ice to slow them
        return this.canCast('shield') ? 'shield' : 'iceShard';
      case 'iceShard':
        // Counter ice with shield, or blast through with fireball
        return this.canCast('shield') ? 'shield' : 'fireball';
      case 'shield':
      case 'healSpell':
      case 'reflect':
      case 'teleport':
      case 'superShield':
        // Player is defending/healing/utility - press the attack
        return this.canCast('fireball') ? 'fireball' : 'iceShard';
    }
  }

  update(_delta: number): CPUAction | null {
    const now = Date.now();

    this.cooldowns.forEach((lastCast, spell) => {
      if (now - lastCast > this.getCooldownDuration(spell)) {
        this.cooldowns.set(spell, 0);
      }
    });

    // Priority 1: React to incoming projectiles (shield)
    if (this.incomingProjectile) {
      const timeSinceProjectile = now - this.incomingProjectileTime;

      if (timeSinceProjectile >= this.reactionTime) {
        if (this.canCast('shield') && Math.random() < this.shieldProbability) {
          this.incomingProjectile = false;
          this.cooldowns.set('shield', now);
          this.justShielded = true;
          this.shieldTime = now;
          return { type: 'cast', spell: 'shield' };
        }
      }

      if (timeSinceProjectile > 1500) {
        this.incomingProjectile = false;
      }
    }

    // Priority 2: Counter-attack after shielding (personality-dependent)
    if (this.justShielded && now - this.shieldTime < CPUOpponent.COUNTER_WINDOW_MS) {
      if (Math.random() < this.personality.counterAttackBias) {
        const counterSpell = this.chooseAttackSpell();
        if (counterSpell && this.canCast(counterSpell)) {
          this.justShielded = false;
          this.lastActionTime = now;
          this.currentDelay = this.minActionDelay * 0.7; // Faster counter-attack
          this.cooldowns.set(counterSpell, now);
          return { type: 'cast', spell: counterSpell };
        }
      }
    }
    if (this.justShielded && now - this.shieldTime >= CPUOpponent.COUNTER_WINDOW_MS) {
      this.justShielded = false;
    }

    // Respect action delay
    if (now - this.lastActionTime < this.currentDelay) {
      return null;
    }

    // Priority 3: Pattern-based prediction and tactical response
    const predictedSpell = this.predictPlayerNextSpell();
    if (predictedSpell && Math.random() < this.personality.patternExploitRate) {
      const counterSpell = this.getCounterSpell(predictedSpell);
      if (this.canCast(counterSpell)) {
        this.lastActionTime = now;
        this.currentDelay = this.minActionDelay + Math.random() * (this.maxActionDelay - this.minActionDelay);
        this.cooldowns.set(counterSpell, now);
        return { type: 'cast', spell: counterSpell };
      }
    }

    // Priority 4: Tactical - attack when player shield is down
    if (!this.playerShieldActive && Math.random() < this.aggressiveness * 1.2) {
      const spell = this.chooseAttackSpell();
      if (spell && this.canCast(spell)) {
        this.lastActionTime = now;
        this.currentDelay = this.minActionDelay + Math.random() * (this.maxActionDelay - this.minActionDelay);
        this.cooldowns.set(spell, now);
        return { type: 'cast', spell };
      }
    }

    // Priority 5: Attempt ultimate spell (~10% chance when available)
    if (Math.random() < 0.10) {
      const ultimate = this.chooseUltimateSpell();
      if (ultimate && this.canCast(ultimate)) {
        this.lastActionTime = now;
        this.currentDelay = this.minActionDelay + Math.random() * (this.maxActionDelay - this.minActionDelay);
        this.cooldowns.set(ultimate, now);
        return { type: 'cast', spell: ultimate };
      }
    }

    // Priority 6: Standard aggression-based attack
    if (Math.random() < this.aggressiveness) {
      const spell = this.chooseAttackSpell();
      if (spell && this.canCast(spell)) {
        this.lastActionTime = now;
        this.currentDelay = this.minActionDelay + Math.random() * (this.maxActionDelay - this.minActionDelay);
        this.cooldowns.set(spell, now);
        return { type: 'cast', spell };
      }
    }

    return { type: 'idle' };
  }

  private chooseUltimateSpell(): SpellType | null {
    const config = WIZARD_CONFIGS[this.wizardType];
    const ultimates: SpellType[] = [];

    if (this.canCast('meteor') && config.meteorDamage > 0) ultimates.push('meteor');
    if (this.canCast('blizzard') && config.blizzardDamage > 0) ultimates.push('blizzard');
    if (this.canCast('superShield') && config.superShieldStrength > 0) ultimates.push('superShield');

    if (ultimates.length === 0) return null;

    // Prefer defensive ultimate when health is low
    if (this.incomingProjectile && ultimates.includes('superShield')) {
      return 'superShield';
    }

    return ultimates[Math.floor(Math.random() * ultimates.length)];
  }

  private chooseAttackSpell(): SpellType | null {
    const config = WIZARD_CONFIGS[this.wizardType];
    const availableSpells: { spell: SpellType; weight: number }[] = [];

    if (this.canCast('fireball')) {
      let weight = config.fireballDamage / 25;
      if (this.personality.preferredSpell === 'fireball') weight *= this.personality.spellBias;
      availableSpells.push({ spell: 'fireball', weight });
    }

    if (this.canCast('iceShard')) {
      let weight = config.iceShardDamage / 20;
      if (this.personality.preferredSpell === 'iceShard') weight *= this.personality.spellBias;
      // Frostmage prefers ice when player is not slowed
      if (this.wizardType === 'frostmage') weight *= 1.3;
      availableSpells.push({ spell: 'iceShard', weight });
    }

    if (availableSpells.length === 0) return null;

    const totalWeight = availableSpells.reduce((sum, s) => sum + s.weight, 0);
    let random = Math.random() * totalWeight;

    for (const { spell, weight } of availableSpells) {
      random -= weight;
      if (random <= 0) return spell;
    }

    return availableSpells[0].spell;
  }

  private canCast(spell: SpellType): boolean {
    const lastCast = this.cooldowns.get(spell) || 0;
    if (lastCast === 0) return true;
    return Date.now() - lastCast >= this.getCooldownDuration(spell);
  }

  private getCooldownDuration(spell: SpellType): number {
    const config = WIZARD_CONFIGS[this.wizardType];
    let baseDuration: number;
    switch (spell) {
      case 'fireball':
        baseDuration = config.fireballCooldown;
        break;
      case 'iceShard':
        baseDuration = config.iceShardCooldown;
        break;
      case 'shield':
        baseDuration = config.shieldCooldown;
        break;
      case 'lightningBolt':
        baseDuration = config.lightningBoltCooldown;
        break;
      case 'healSpell':
        baseDuration = config.healCooldown;
        break;
      case 'meteor':
        baseDuration = config.meteorCooldown;
        break;
      case 'superShield':
        baseDuration = config.superShieldCooldown;
        break;
      case 'blizzard':
        baseDuration = config.blizzardCooldown;
        break;
      default:
        // reflect, teleport, or any future spells — use a reasonable default
        baseDuration = 3000;
        break;
    }
    // Apply slow effect from cooldown manager
    const slowMultiplier = this.cooldownManager?.getEffectiveSlowMultiplier() ?? 1;
    return baseDuration * slowMultiplier;
  }

  isSlowed(): boolean {
    return this.cooldownManager?.isSlowed() ?? false;
  }

  getDifficulty(): { reaction: number; aggression: number } {
    return {
      reaction: this.reactionTime,
      aggression: this.aggressiveness
    };
  }

  reset(): void {
    this.cooldowns.clear();
    this.initializeCooldowns();
    this.lastActionTime = 0;
    this.incomingProjectile = false;
    this.incomingProjectileTime = 0;
    this.playerSpellHistory = [];
    this.justShielded = false;
    this.playerShieldActive = false;
  }
}
